import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { getLivePulseSnapshot, getNationRivalry } from "@/lib/social.functions";
import { getTopViralClips } from "@/lib/worldPulseLive.functions";

/**
 * FOMO Layer™ — rotating "live signal" strip.
 * Combines real counters from world_pulse / hype_pulses / viral_clips and
 * cycles them every 4s. Pure UI, no DB writes.
 */
export function FomoLayer() {
  const pulse = useQuery({
    queryKey: ["fomo", "pulse"],
    queryFn: () => getLivePulseSnapshot(),
    refetchInterval: 15_000,
  });
  const nations = useQuery({
    queryKey: ["fomo", "nations"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 30_000,
  });
  const clips = useQuery({
    queryKey: ["fomo", "clips"],
    queryFn: () => getTopViralClips(),
    refetchInterval: 20_000,
  });

  const fans = pulse.data?.active_fans ?? 0;
  const burst = pulse.data?.hype_burst ?? 0;
  const topNation = nations.data?.rows?.[0];
  const topClip = clips.data?.rows?.[0];
  const clipsCount = (clips.data?.rows ?? []).length;
  const shares = (clips.data?.rows ?? []).reduce((acc, c) => acc + (c.shares ?? 0), 0);

  const messages = [
    fans > 0 && `🌍 ${fans.toLocaleString()} fans en vivo ahora`,
    burst > 0 && `⚡ ${burst} naciones vibrando esta hora`,
    topNation && `📈 ${topNation.nation_code} subiendo con ${(topNation.hype_total ?? 0).toLocaleString()} hype`,
    clipsCount > 0 && `🎬 ${clipsCount} clips creados en las últimas 6h`,
    shares > 0 && `🔁 ${shares.toLocaleString()} shares en cadena`,
    topClip && topClip.home_team && topClip.away_team && `🔥 Trending: ${topClip.home_team} vs ${topClip.away_team}`,
  ].filter(Boolean) as string[];

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (messages.length === 0) return;
    const id = window.setInterval(() => setIdx((i) => (i + 1) % messages.length), 4000);
    return () => window.clearInterval(id);
  }, [messages.length]);

  if (messages.length === 0) return null;
  const current = messages[idx % messages.length];

  return (
    <div
      aria-live="polite"
      className="relative overflow-hidden rounded-full border border-white/10 bg-gradient-to-r from-rose-500/15 via-fuchsia-500/10 to-orange-500/15 px-4 py-1.5 backdrop-blur"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_0%_50%,rgba(244,114,55,.25),transparent_60%)]" />
      <div className="relative flex items-center gap-2 text-xs font-bold tracking-wide text-white">
        <Flame className="h-3.5 w-3.5 shrink-0 animate-pulse text-orange-400" />
        <span key={current} className="animate-fade-in truncate">{current}</span>
      </div>
    </div>
  );
}